import React, { useMemo } from 'react';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import './DatePickerCalendar.css';

/**
 * Get weekStartsOn for react-day-picker (0=Sunday, 1=Monday, ...) from locale.
 * Uses Intl.Locale.getWeekInfo() when available; otherwise defaults to Monday (1).
 */
function getWeekStartsOn(locale) {
  try {
    const loc = new Intl.Locale(locale ?? navigator.language);
    if (typeof loc.getWeekInfo === 'function') {
      const { firstDay } = loc.getWeekInfo();
      return firstDay === 7 ? 0 : firstDay;
    }
  } catch (_) {}
  return 1;
}

function DatePickerCalendar({
  selectedDate,
  openAbove = false,
  onDateSelect,
  onTodayClick,
  onTomorrowClick,
  onLaterClick,
  onNoDueDateClick
}) {
  // Helper function to parse date string (YYYY-MM-DD or ISO string) to Date object in local timezone
  const parseDateString = (dateString) => {
    if (!dateString) return null;
    const dateStr = dateString.split('T')[0];
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  const handleTodayClick = (e) => {
    e.stopPropagation();
    if (onTodayClick) {
      onTodayClick();
    }
  };

  const handleTomorrowClick = (e) => {
    e.stopPropagation();
    if (onTomorrowClick) {
      onTomorrowClick();
    }
  };

  const handleLaterClick = (e) => {
    e.stopPropagation();
    if (onLaterClick) {
      onLaterClick();
    }
  };

  const handleNoDueDateClick = (e) => {
    e.stopPropagation();
    if (onNoDueDateClick) {
      onNoDueDateClick();
    }
  };

  const handleDateSelect = (date) => {
    if (date && onDateSelect) {
      onDateSelect(date);
    }
  };

  const weekStartsOn = useMemo(() => getWeekStartsOn(), []);

  // Start of today (00:00) so past dates are disabled but today remains selectable
  const startOfToday = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const quickActions = (
    <div className="date-picker-calendar-quick-actions">
      <button
        type="button"
        className="date-picker-calendar-quick-button text-non-selectable"
        onClick={handleTodayClick}
      >
        Today
      </button>
      <button
        type="button"
        className="date-picker-calendar-quick-button text-non-selectable"
        onClick={handleTomorrowClick}
      >
        Tomorrow
      </button>
      <button
        type="button"
        className="date-picker-calendar-quick-button text-non-selectable"
        onClick={handleLaterClick}
      >
        Later
      </button>
    </div>
  );

  const picker = (
    <div className="date-picker-calendar-picker">
      <DayPicker
        mode="single"
        selected={selectedDate ? parseDateString(selectedDate) : undefined}
        onSelect={handleDateSelect}
        defaultMonth={selectedDate ? parseDateString(selectedDate) : new Date()}
        weekStartsOn={weekStartsOn}
        disabled={{ before: startOfToday }}
      />
    </div>
  );

  const clearAction = (
    <div className="date-picker-calendar-clear-action">
      <button
        type="button"
        className="date-picker-calendar-clear-button text-non-selectable"
        onClick={handleNoDueDateClick}
      >
        No due date
      </button>
    </div>
  );

  return (
    <div className={`date-picker-calendar text-non-selectable ${openAbove ? 'date-picker-calendar-open-above' : ''}`}>
      {openAbove ? (
        <>
          {clearAction}
          {picker}
          {quickActions}
        </>
      ) : (
        <>
          {quickActions}
          {picker}
          {clearAction}
        </>
      )}
    </div>
  );
}

export default DatePickerCalendar;

